import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Clock, Car, Home, RefreshCw, Users, Calendar, TrendingUp, BarChart3 } from 'lucide-react';

const SEVERITY_COLORS = { HIGH: 'bg-red-500 text-white', MEDIUM: 'bg-amber-500 text-white', LOW: 'bg-slate-400 text-white' };
const RISK_COLORS = { BLOCK: 'bg-red-500 text-white', WARNING: 'bg-amber-500 text-white' };

export default function RosterInsightsPage() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [continuityData, setContinuityData] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/roster/intelligence/insights');
      setInsights(res.data.data);

      // Load continuity for participants with shifts
      const participants = await api.get('/participants?page=1&pageSize=20');
      const items = participants.data.items || participants.data.data || [];
      const contResults = [];
      for (const p of items.slice(0, 10)) {
        try {
          const cr = await api.get(`/roster/intelligence/participants/${p.id}/continuity?days=30`);
          contResults.push({ ...cr.data.data, participantName: `${p.first_name} ${p.last_name}` });
        } catch { /* skip */ }
      }
      setContinuityData(contResults.filter(c => c.totalShifts > 0));
    } catch { toast.error('Failed to load insights'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const s = insights?.summary || {};
  const goodCount = continuityData.filter(c => c.continuityLevel === 'GOOD').length;
  const modCount = continuityData.filter(c => c.continuityLevel === 'MODERATE').length;
  const lowCount = continuityData.filter(c => c.continuityLevel === 'LOW').length;

  return (
    <div className="space-y-6" data-testid="roster-insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="w-6 h-6" />Roster Intelligence</h1>
          <p className="text-muted-foreground mt-1">Week: {insights?.weekRange?.start} to {insights?.weekRange?.end}</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-insights"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card><CardContent className="py-4 text-center"><p className="text-3xl font-bold">{s.totalUpcomingShifts || 0}</p><p className="text-xs text-muted-foreground mt-1">Upcoming Shifts</p></CardContent></Card>
        <Card className="border-red-200"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-red-600">{s.unassignedShifts || 0}</p><p className="text-xs text-muted-foreground mt-1">Unassigned</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-amber-600">{s.staffWithHighOvertime || 0}</p><p className="text-xs text-muted-foreground mt-1">High Overtime</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-purple-600">{s.understaffedHouses || 0}</p><p className="text-xs text-muted-foreground mt-1">Houses at Risk</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-3xl font-bold">{insights?.travelInefficiencyScore || 0}%</p><p className="text-xs text-muted-foreground mt-1">Travel Score</p></CardContent></Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Shifts At Risk */}
        <Card data-testid="shifts-at-risk-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Shifts At Risk ({insights?.shiftsAtRiskCount || 0})</CardTitle></CardHeader>
          <CardContent>
            {insights?.shiftsAtRisk?.length > 0 ? (
              <div className="space-y-2">
                {insights.shiftsAtRisk.slice(0, 8).map(sr => (
                  <div key={sr.shiftId} className="flex items-center justify-between text-sm border-b border-dashed pb-2 last:border-0">
                    <div>
                      <span className="font-mono text-xs">{sr.date}</span>
                      <span className="ml-2">{sr.startTime}-{sr.endTime}</span>
                    </div>
                    <Badge variant={sr.daysUntil <= 1 ? 'destructive' : 'outline'}>{sr.daysUntil === 0 ? 'Today' : sr.daysUntil === 1 ? 'Tomorrow' : `${sr.daysUntil}d`}</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm py-4 text-center">No shifts at risk</p>}
          </CardContent>
        </Card>

        {/* Travel Inefficiency */}
        <Card data-testid="travel-inefficiency-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Car className="w-4 h-4 text-amber-500" />Travel Inefficiency</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" className="text-muted" strokeWidth="8" />
                  <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" className={insights?.travelInefficiencyScore > 50 ? 'text-red-500' : insights?.travelInefficiencyScore > 20 ? 'text-amber-500' : 'text-green-500'} strokeWidth="8" strokeDasharray={`${(insights?.travelInefficiencyScore || 0) * 2.51} 251`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-bold">{insights?.travelInefficiencyScore || 0}%</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>Staff with multiple shifts per day requiring travel between locations.</p>
                <p className="mt-1">{insights?.travelInefficiencyScore > 50 ? 'High multi-location scheduling detected.' : insights?.travelInefficiencyScore > 20 ? 'Moderate travel between shifts.' : 'Travel patterns are efficient.'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* High Overtime Staff */}
        <Card data-testid="high-overtime-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" />High Overtime Staff ({insights?.highOvertimeStaffCount || 0})</CardTitle></CardHeader>
          <CardContent>
            {insights?.highOvertimeStaff?.length > 0 ? (
              <div className="space-y-2">
                {insights.highOvertimeStaff.map(h => (
                  <div key={h.staffId} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{h.staffName}</p>
                      <p className="text-xs text-muted-foreground">{h.hoursThisWeek}h / {h.maxHours}h</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden"><div className={`h-full rounded-full ${h.utilizationPct > 100 ? 'bg-red-500' : h.utilizationPct > 90 ? 'bg-amber-500' : 'bg-green-500'}`} style={{width: `${Math.min(h.utilizationPct, 100)}%`}} /></div>
                      <Badge className={RISK_COLORS[h.risk] || 'bg-slate-400 text-white'}>{h.utilizationPct}%</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm py-4 text-center">No staff at overtime risk</p>}
          </CardContent>
        </Card>

        {/* Understaffed Houses */}
        <Card data-testid="understaffed-houses-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Home className="w-4 h-4 text-purple-500" />Understaffed Houses ({insights?.housesUnderstaffed?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            {insights?.housesUnderstaffed?.length > 0 ? (
              <div className="space-y-2">
                {insights.housesUnderstaffed.map(h => (
                  <div key={h.houseId} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{h.houseName}</p>
                      <p className="text-xs text-muted-foreground">{h.residentCount} residents · {h.assignedShifts} shifts assigned</p>
                    </div>
                    <Badge className={SEVERITY_COLORS[h.severity]}>{h.coveragePercentage}%</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm py-4 text-center">All houses adequately staffed</p>}
          </CardContent>
        </Card>

        {/* Continuity Score Distribution */}
        <Card className="lg:col-span-2" data-testid="continuity-distribution-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><RefreshCw className="w-4 h-4 text-green-500" />Continuity Score Distribution</CardTitle></CardHeader>
          <CardContent>
            {continuityData.length > 0 ? (
              <div className="space-y-4">
                {/* Distribution bars */}
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-green-600 font-medium">Good (&gt;70%)</span><span>{goodCount}</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{width: `${continuityData.length > 0 ? (goodCount / continuityData.length * 100) : 0}%`}} /></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-amber-600 font-medium">Moderate (40-70%)</span><span>{modCount}</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{width: `${continuityData.length > 0 ? (modCount / continuityData.length * 100) : 0}%`}} /></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-red-600 font-medium">Low (&lt;40%)</span><span>{lowCount}</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-red-500 rounded-full" style={{width: `${continuityData.length > 0 ? (lowCount / continuityData.length * 100) : 0}%`}} /></div>
                  </div>
                </div>
                {/* Participant list */}
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {continuityData.sort((a, b) => a.continuityScore - b.continuityScore).map(c => (
                    <div key={c.participantId} className="flex items-center justify-between text-sm">
                      <span>{c.participantName}</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-xs ${c.continuityLevel === 'GOOD' ? 'text-green-600' : c.continuityLevel === 'MODERATE' ? 'text-amber-600' : 'text-red-600'}`}>{c.continuityScore}%</span>
                        <Badge variant={c.continuityLevel === 'GOOD' ? 'default' : 'outline'} className="text-[10px]">{c.continuityLevel}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted-foreground text-sm py-4 text-center">No continuity data available</p>}
          </CardContent>
        </Card>

        {/* Branches With Shortage */}
        {insights?.branchesWithShortage?.length > 0 && (
          <Card className="lg:col-span-2" data-testid="branches-shortage-card">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Branch Staffing Shortage</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {insights.branchesWithShortage.map(b => (
                  <div key={b.branchId} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{b.branchId}</p>
                      <p className="text-xs text-muted-foreground">{b.unassignedShifts} of {b.totalShifts} unassigned</p>
                    </div>
                    <Badge className={SEVERITY_COLORS[b.severity]}>{b.shortagePercentage}%</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
